import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import FileUpload from '../components/FileUpload';
import FaqAccordion from '../components/FaqAccordion';
import ShareButtons from '../components/ShareButtons';
import { trackEvent } from '../utils/analytics';
import { Download, ArrowRight, RotateCcw, Scissors, Check, XCircle } from 'lucide-react';
 import { Helmet } from 'react-helmet-async';
import { downloadFile } from '../utils/download';
 
export default function SplitPdf() {
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSplit = async () => {
    if (!files.length) return toast.error('Please select a PDF file');
    trackEvent('conversion_started', { tool_name: 'Split PDF' });
    setStatus('processing'); setError(''); setResult(null);

    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const res = await axios.post('/api/convert/split', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStatus('done');
      setResult(res.data);
      toast.success(`PDF split into ${res.data.pageCount} pages!`);
    } catch (err) {
      setStatus('error');
      setError((err.response && err.response.data && err.response.data.error) || 'Split failed');
    }
  };

  const handleReset = () => { setFiles([]); setStatus(null); setResult(null); setError(''); };

  return (
    <div>
      <Helmet>
  <title>Split PDF Online Free — Extract Any Page | ILoveConvert</title>
  <meta name="description" content="Split PDF into individual pages or sections. Free, fast, no signup needed. Works on mobile too." />
  <meta name="keywords" content="split pdf, extract pdf pages, pdf splitter, separate pdf pages, split pdf online free" />
  <link rel="canonical" href="https://iloveconvert.in/split-pdf" />
  <meta property="og:title" content="Split PDF Online Free — Extract Any Page | ILoveConvert" />
  <meta property="og:description" content="Split PDF into individual pages or sections. Free, fast, no signup needed." />
  <meta property="og:url" content="https://iloveconvert.in/split-pdf" />
  <meta property="og:type" content="website" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Split PDF Online Free — Extract Any Page" />
  <meta name="twitter:description" content="Split PDF into individual pages or sections. Free, no signup needed." />
</Helmet>

      <section className="hero" style={{ padding: '40px 20px' }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><Scissors size={44} strokeWidth={1.5} /></div>
        <h1 style={{ fontSize: 'clamp(1.6rem,4vw,2.4rem)', marginBottom: 8 }}>Split PDF</h1>
        <p>Extract every page of your PDF as a separate file</p>
      </section>
<div style={{ maxWidth: 900, margin: '20px auto', padding: '0 20px' }}>
        <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
          Split PDF files online easily and extract individual pages in seconds.
          This tool helps you break large PDF documents into smaller files for easier
          sharing, editing, or storage. Upload your PDF and download each page separately.
        </p>
      </div>

      
      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px' }}>

        <h2>How to split a PDF file</h2>
        <ul>
          <li>Upload your PDF file</li>
          <li>Click the “Split PDF” button</li>
          <li>Wait for processing</li>
          <li>Download individual pages instantly</li>
        </ul>

        <h2>Why use this Split PDF tool?</h2>
        <ul>
          <li>Fast and secure processing</li>
          <li>No installation required</li>
          <li>Works on mobile and desktop</li>
          <li>Extract pages without quality loss</li>
        </ul>

        <h2>Best use cases</h2>
        <ul>
          <li>Extract specific pages from documents</li>
          <li>Separate large PDF reports</li>
          <li>Share only required pages</li>
          <li>Organize scanned files</li>
        </ul>

        <h2>Frequently asked questions</h2>

        <FaqAccordion faqs={[
          { q: 'Is this tool free?', a: 'Yes, it is completely free to use.' },
          { q: 'Are my files secure?', a: 'Yes, files are processed securely and deleted automatically.' },
          { q: 'Can I split PDF on mobile?', a: 'Yes, it works on all devices.' },
        ]} />

      </div>
      <div className="upload-page">
        {!status && (
          <>
            <FileUpload onFiles={setFiles} accept={{ 'application/pdf': ['.pdf'] }} />
            {files.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <button className="btn btn-primary btn-lg" onClick={handleSplit} style={{ width: '100%' }}>
                  <ArrowRight size={18} /> Split PDF into Pages
                </button>
              </div>
            )}
          </>
        )}

        {status === 'processing' && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="spinner" />
            <p style={{ marginTop: 20, fontWeight: 600 }}>Splitting your PDF...</p>
          </div>
        )}

        {status === 'done' && result && (
          <>
            <div className="result-box">
              <div className="check"><Check size={32} strokeWidth={3} /></div>
              <h3>PDF Split Successfully!</h3>
              <p>Your PDF was split into {result.pageCount} individual page{result.pageCount > 1 ? 's' : ''}.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                {result.downloadUrls.map((url, i) => (
                  <button key={i} onClick={() => downloadFile(url, url.split('/').pop(), 'Split PDF')} className="download-btn" style={{ justifyContent: 'center' }}>
                    <Download size={16} /> Download Page {i + 1}
                  </button>
                ))}
              </div>
              <ShareButtons toolLabel="Split PDF" />
            </div>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button className="btn btn-outline" onClick={handleReset}><RotateCcw size={16} /> Split Another</button>
            </div>
          </>
        )}

        {status === 'error' && (
          <div style={{ background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: 28, textAlign: 'center' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: '#dc2626' }}><XCircle size={40} /></div>
            <h3 style={{ color: '#dc2626', fontFamily: 'Sora, sans-serif', marginBottom: 8 }}>Split Failed</h3>
            <p style={{ color: '#b91c1c', marginBottom: 20 }}>{error}</p>
            <button className="btn btn-primary" onClick={handleReset}><RotateCcw size={16} /> Try Again</button>
          </div>
        )}

        {/* <AdInline /> */}
      </div>
    </div>
  );
}
